
import { useState, useEffect, useCallback } from 'react';
import {
  CertificateTemplate,
  CertificateTemplateType,
  CertificateTemplatePreview
} from '@/types/certificateTemplate';
import { certificateTemplateLocalStorageService } from '@/services/certification/templates/localStorage';
import { toast } from 'sonner';

export const useCertificateTemplates = (type?: CertificateTemplateType) => {
  const [templates, setTemplates] = useState<CertificateTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<CertificateTemplate | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Initialize the certificate template service with enhanced capacity (5000+)
  const initializeCertificateData = async () => {
    console.info('🚀 Initializing certificate templates with 5000+ localStorage capacity...');
    setIsLoading(true);
    
    try {
      await certificateTemplateLocalStorageService.initialize();
      await loadTemplates();
      setInitialized(true);
    } catch (error) {
      console.error('❌ Error initializing certificate templates:', error);
      setError(error instanceof Error ? error : new Error('Failed to initialize certificate templates'));
    } finally {
      setIsLoading(false);
    }
  };

  // Load templates based on type with enhanced localStorage persistence
  const loadTemplates = useCallback(async (showToast = false) => {
    if (!initialized && !isLoading) return;

    try {
      console.log('📋 Loading certificate templates with 5000+ localStorage capacity...');
      
      let loadedTemplates: CertificateTemplate[];
      
      if (type) {
        loadedTemplates = await certificateTemplateLocalStorageService.getCertificateTemplatesByType(type);
      } else {
        loadedTemplates = await certificateTemplateLocalStorageService.getAllCertificateTemplates();
      }
      
      console.log(`✅ Loaded ${loadedTemplates.length} certificate templates from localStorage`);
      setTemplates(loadedTemplates);
      
      // Set the default template if one exists
      if (type && loadedTemplates.length > 0) {
        const defaultTemplate = loadedTemplates.find(t => t.isDefault) || loadedTemplates[0];
        setSelectedTemplate(defaultTemplate);
      }
      
      if (showToast) {
        toast.success(`Loaded ${loadedTemplates.length} certificate templates`);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading certificate templates:', err);
      setError(err instanceof Error ? err : new Error('Failed to load certificate templates'));
      if (showToast) {
        toast.error('Failed to load certificate templates');
      }
    }
  }, [type, initialized, isLoading]);

  // Enhanced refresh with better error handling
  const refreshTemplates = async () => {
    setIsRefreshing(true);
    try {
      await loadTemplates(true);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Initialize on mount
  useEffect(() => {
    if (!initialized) {
      initializeCertificateData();
    }
  }, [initialized]);

  // Listen for real-time certificate template changes
  useEffect(() => {
    const handleTemplatesChanged = () => {
      console.log('🔄 Certificate templates change detected, refreshing...');
      loadTemplates();
    };

    window.addEventListener('certificateTemplatesChanged', handleTemplatesChanged);

    return () => {
      window.removeEventListener('certificateTemplatesChanged', handleTemplatesChanged);
    };
  }, [loadTemplates]);

  // Get all template previews with enhanced error handling
  const getTemplatePreviews = useCallback((): CertificateTemplatePreview[] => {
    try {
      return certificateTemplateLocalStorageService.getCertificateTemplatePreviews();
    } catch (err) {
      console.error('Error getting certificate template previews:', err);
      setError(err instanceof Error ? err : new Error('Failed to get certificate template previews'));
      return [];
    }
  }, []);

  // Get template by ID with enhanced persistence
  const getTemplateById = useCallback((id: string): CertificateTemplate | null => {
    try {
      return certificateTemplateLocalStorageService.getCertificateTemplateById(id);
    } catch (err) {
      console.error('Error getting certificate template by ID:', err);
      setError(err instanceof Error ? err : new Error('Failed to get certificate template by ID'));
      return null;
    }
  }, []);

  // Create a new template with enhanced localStorage persistence
  const createTemplate = useCallback(async (template: Omit<CertificateTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<CertificateTemplate | null> => {
    try {
      console.log('📋 Creating certificate template with enhanced localStorage persistence...');
      
      const newTemplate = await certificateTemplateLocalStorageService.createCertificateTemplate(template);
      setTemplates(prev => [...prev, newTemplate]);
      
      toast.success(`Certificate template "${newTemplate.name}" created successfully`);
      
      // Emit change event for real-time updates
      window.dispatchEvent(new Event('certificateTemplatesChanged'));
      
      return newTemplate;
    } catch (err) {
      console.error('Error creating certificate template:', err);
      setError(err instanceof Error ? err : new Error('Failed to create certificate template'));
      toast.error('Failed to create certificate template');
      return null;
    }
  }, []);

  // Update a template with enhanced localStorage persistence
  const updateTemplate = useCallback(async (id: string, updates: Partial<Omit<CertificateTemplate, 'id' | 'createdAt' | 'updatedAt'>>): Promise<CertificateTemplate | null> => {
    try {
      const updatedTemplate = await certificateTemplateLocalStorageService.updateCertificateTemplate(id, updates);
      if (updatedTemplate) {
        setTemplates(prev => prev.map(t => t.id === id ? updatedTemplate : t));
        if (selectedTemplate?.id === id) {
          setSelectedTemplate(updatedTemplate);
        }
        
        toast.success('Certificate template updated successfully');
        
        // Emit change event for real-time updates
        window.dispatchEvent(new Event('certificateTemplatesChanged'));
      }
      return updatedTemplate;
    } catch (err) {
      console.error('Error updating certificate template:', err);
      setError(err instanceof Error ? err : new Error('Failed to update certificate template'));
      toast.error('Failed to update certificate template');
      return null;
    }
  }, [selectedTemplate]);

  // Delete a template with enhanced localStorage persistence
  const deleteTemplate = useCallback(async (id: string): Promise<boolean> => {
    try {
      const success = await certificateTemplateLocalStorageService.deleteCertificateTemplate(id);
      if (success) {
        setTemplates(prev => prev.filter(t => t.id !== id));
        if (selectedTemplate?.id === id) {
          const remainingTemplates = templates.filter(t => t.id !== id);
          setSelectedTemplate(remainingTemplates.length > 0 ? remainingTemplates[0] : null);
        }
        
        toast.success('Certificate template deleted successfully');
        
        // Emit change event for real-time updates
        window.dispatchEvent(new Event('certificateTemplatesChanged'));
      }
      return success;
    } catch (err) {
      console.error('Error deleting certificate template:', err);
      setError(err instanceof Error ? err : new Error('Failed to delete certificate template'));
      toast.error('Failed to delete certificate template');
      return false;
    }
  }, [templates, selectedTemplate]);

  // Set default template with enhanced localStorage persistence
  const setDefaultTemplate = useCallback(async (id: string): Promise<boolean> => {
    try {
      const success = await certificateTemplateLocalStorageService.setDefaultCertificateTemplate(id);
      if (success) {
        setTemplates(prev => prev.map(t => ({
          ...t,
          isDefault: t.id === id
        })));
        
        toast.success('Default template updated successfully');
        
        // Emit change event for real-time updates
        window.dispatchEvent(new Event('certificateTemplatesChanged'));
      }
      return success;
    } catch (err) {
      console.error('Error setting default certificate template:', err);
      setError(err instanceof Error ? err : new Error('Failed to set default certificate template'));
      toast.error('Failed to set default template');
      return false;
    }
  }, []);

  // Duplicate a template with enhanced localStorage persistence
  const duplicateTemplate = useCallback(async (id: string): Promise<CertificateTemplate | null> => {
    try {
      const duplicatedTemplate = await certificateTemplateLocalStorageService.duplicateCertificateTemplate(id);
      if (duplicatedTemplate) {
        setTemplates(prev => [...prev, duplicatedTemplate]);
        
        toast.success(`Template duplicated as "${duplicatedTemplate.name}"`);
        
        // Emit change event for real-time updates
        window.dispatchEvent(new Event('certificateTemplatesChanged'));
      }
      return duplicatedTemplate;
    } catch (err) {
      console.error('Error duplicating certificate template:', err);
      setError(err instanceof Error ? err : new Error('Failed to duplicate certificate template'));
      toast.error('Failed to duplicate certificate template');
      return null;
    }
  }, []);

  return {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    isLoading,
    isRefreshing,
    error,
    initialized,
    refreshTemplates,
    getTemplatePreviews,
    getTemplateById,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    setDefaultTemplate,
    duplicateTemplate
  };
};

export default useCertificateTemplates;
